import {forwardRef, useCallback, useEffect} from 'react'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import Swal from 'sweetalert2'
import {ID, isNotEmpty, KTSVG, QUERIES, getAttachmentUrl} from '../../../../../../_metronic/helpers'
import {CustomToggleProps} from '../../../../wiki/components/wiki-list/table/WikiTable'
import {Toast} from '../../../../../../_metronic/partials/widgets'
import {deleteAttachement, getAttachements} from '../TaskAttachment/core/_requests'
import {createTaskAttachement} from '../core/_requests'
import {
  getTaskAttachmentError,
  TASK_ATTACHMENT_ACCEPT,
} from '../../../../../../_metronic/helpers/taskAttachment'

export type PendingAttachment = {file: File | ''}

export type ReadOnlyAttachmentSection = {
  title: string
  attachments: Array<{id: number; file: string; file_name: string}>
}

type Props = {
  taskId?: ID
  /** 'live' (default): comportamento original, upload imediato contra um taskId real.
   *  'draft': sem chamada de API, lista fica em memória em `items`/`onChange` — usado na
   *  criação de tarefa, onde a tarefa ainda não existe. */
  mode?: 'live' | 'draft'
  items?: Array<PendingAttachment>
  onChange?: (items: Array<PendingAttachment>) => void
  disabled?: boolean
  readOnlySections?: Array<ReadOnlyAttachmentSection>
}

const OverviewAttachement = ({
  taskId,
  mode = 'live',
  items = [],
  onChange,
  disabled,
  readOnlySections,
}: Props) => {
  const intl = useIntl()

  const initialValues = {
    file: '',
    task_id: '',
  }

  const queryClient = useQueryClient()

  // Mesma regra do servidor (tipo e 20MB): recusa na seleção e diz qual arquivo e por quê.
  const rejectInvalidFile = (file: File): boolean => {
    const error = getTaskAttachmentError(file)
    if (!error) {
      return false
    }
    Toast.fire({
      icon: 'error',
      title: error,
      timer: 8000,
      customClass: {
        popup: 'bg-light-danger',
        title: 'text-danger',
      },
    })
    return true
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.file)) {
          await createTaskAttachement(values).then((response) => {
            Toast.fire({
              icon: 'success',
              title: intl.formatMessage(
                {id: 'ALERT.LABEL.SUCCESS_CREATE_ITEM'},
                {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASKATTACHEMENT'})}
              ),
              customClass: {
                popup: 'bg-light-success',
                title: 'text-success',
              },
            })
            queryClient.invalidateQueries(`${QUERIES.TASKS_ATTACHEMENT_LIST}`)
            resetForm()
          })
        }
      } catch (error: any) {
        console.error(error)

        // Verifica se há erros estruturados na resposta
        if (error.errors && Object.keys(error.errors).length > 0) {
          // Um único aviso com todos os motivos: um Toast novo substitui o anterior, então um aviso
          // por erro deixava o usuário ver só o último (ex.: o tipo, escondendo o tamanho).
          const reasons = Object.values(error.errors)
            .flatMap((messages) => (Array.isArray(messages) ? (messages as string[]) : []))
            .filter((message, index, all) => all.indexOf(message) === index)
          Toast.fire({
            icon: 'error',
            title: reasons.join(' '),
            timer: 8000,
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
        } else if (error.msg) {
          // Exibe a mensagem de erro principal se não houver erros estruturados
          Toast.fire({
            icon: 'error',
            title: error.msg,
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
        } else {
          // Erro genérico
          Toast.fire({
            icon: 'error',
            title: 'Erro ao fazer upload do arquivo',
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
        }
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  useEffect(() => {
    if (mode === 'live') {
      formik.setFieldValue('task_id', taskId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const handleCreate = useCallback(
    async (e: any) => {
      const selected: File | undefined = e.target.files?.[0]
      if (selected && rejectInvalidFile(selected)) {
        e.target.value = null
        return
      }
      formik.setFieldValue('file', e.target.files[0])
      formik.submitForm()
      e.target.value = null
      formik.setFieldValue('task_id', taskId)
    },
    [formik]
  )

  const deleta = async (id: ID) => {
    const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
    let tema = ''
    if (CurrentTheme === 'dark') {
      tema = '#353b48'
    } else {
      tema = '#fff'
    }

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-danger',
      },
      buttonsStyling: false,
    })

    swalWithBootstrapButtons
      .fire({
        title: intl.formatMessage({id: 'ALERT.LABEL.ARE_YOU_SURE'}),
        html: intl.formatMessage({id: 'ALERT.LABEL.IT_CAN_NOT_BE_UNDONE'}),
        icon: 'warning',
        background: tema,
        showCancelButton: true,
        confirmButtonText: 'Sim, pode excluir',
        cancelButtonText: 'Não, cancele!',
        reverseButtons: true,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          return await deleteItem.mutateAsync(id)
        } else if (
          /* Read more about handling dismissals below */
          // @ts-ignore
          result.dismiss === Swal.DismissReason.cancel
        ) {
          Toast.fire({
            icon: 'warning',
            title: intl.formatMessage(
              {id: 'ALERT.LABEL.SUCCESS_CANCEL_ITEM'},
              {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASKATTACHEMENT'})}
            ),
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
        }
      })
  }
  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
  }

  const deleteItem = useMutation((id: ID) => deleteAttachement(id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: () => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_CANCEL_ITEM'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASKATTACHEMENT'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      cancel(true)
    },
  })
  const {refetch, data: response} = useQuery(
    `${QUERIES.TASKS_ATTACHEMENT_LIST}`,
    () => {
      return getAttachements(taskId)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false, enabled: mode === 'live'}
  )

  const CustomToggle = forwardRef((props: CustomToggleProps, ref: React.Ref<HTMLAnchorElement>) => (
    <a
      href=''
      className='text-hover-primary'
      ref={ref}
      onClick={(e) => {
        e.preventDefault()
        props.onClick(e)
      }}
    >
      {props.children}
      <span style={{paddingLeft: '5px'}}></span>
    </a>
  ))

  if (mode === 'draft') {
    const addPending = () => {
      onChange?.([...items, {file: ''}])
    }

    const removePending = (key: number) => {
      onChange?.(items.filter((_, index) => key !== index))
    }

    const updatePending = (key: number, file: File) => {
      onChange?.(
        items.map((item, index) => (index === key ? {file} : item))
      )
    }

    return (
      <>
        {items.map((item, key) => (
          <div className='check d-flex align-items-center mb-4' key={key}>
            <input
              className='form-control form-control-solid py-2  rounded fw-normal'
              type='file'
              accept={TASK_ATTACHMENT_ACCEPT}
              disabled={disabled}
              onKeyPress={(e) => {
                e.key === 'Enter' && e.preventDefault()
              }}
              onChange={(e) => {
                const selected = e.target.files?.[0]
                if (!selected) {
                  return
                }
                if (rejectInvalidFile(selected)) {
                  e.target.value = ''
                  return
                }
                updatePending(key, selected)
              }}
            ></input>

            <button onClick={() => removePending(key)} className='btn p-0 ms-5'>
              <i className='bi bi-trash3-fill'></i>
            </button>
          </div>
        ))}

        <button
          onClick={addPending}
          onKeyPress={(e) => {
            e.key === 'Enter' && e.preventDefault()
          }}
          className='btn text-bold d-flex align-items-center p-0 mt-10 mb-4'
        >
          <div className='d-flex align-items-center justify-content-center w-15px h-15px bg-gray-900 me-3'>
            <i className='bi bi-plus text-gray-100 fs-5 p-0'></i>
          </div>
          {intl.formatMessage({id: 'GENERAL.ADD_ITEM'})}
        </button>

        {readOnlySections?.map(
          (section) =>
            section.attachments.length > 0 && (
              <table
                key={section.title}
                className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer mb-5'
              >
                <thead>
                  <tr className='text-start text-gray-400 fw-bold fs-8 text-uppercase gs-0'>
                    <th className='min-w-300px p-5 shadow-sm '>{section.title}</th>
                  </tr>
                </thead>
                {section.attachments.map((attachment) => {
                  const fileUrl = getAttachmentUrl(attachment.file)
                  return (
                    <tbody key={attachment.id} className='text-gray-600 fw-bold'>
                      <tr>
                        <td colSpan={1}>
                          <div className='d-flex align-items-center px-4'>
                            <KTSVG
                              path='/media/icons/duotune/files/fil003.svg'
                              className='svg-icon svg-icon-2x svg-icon-primary me-4'
                            />
                            <a href={fileUrl} target='_blank' rel='noopener noreferrer'>
                              {attachment.file_name}
                            </a>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )
                })}
              </table>
            )
        )}
      </>
    )
  }

  return (
    <>
      <form id='kt_modal_add_contract_form' className='form' onSubmit={handleCreate} noValidate>
        <div className='row align-items-center mb-4'>
          <div className='col-12 col-md-6 d-flex justify-content-end pe-0'>
            <div className='mb-4'>
              <label className='required fw-bold mb-1'>
                {intl.formatMessage({id: 'FORM.INPUT.NAME.NEW_FILE'})}
              </label>

              <input
                type='file'
                name='file'
                onKeyPress={(e) => {
                  e.key === 'Enter' && e.preventDefault()
                }}
                id='file'
                className='form-control form-control-solid py-2  rounded fw-normal'
                accept={TASK_ATTACHMENT_ACCEPT}
                onChange={handleCreate}
                //   ref={ref}
              />
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12 p-0 shadow bg-body fs-6'>
            <div className='min-h-225px overflow-scroll'>
              {/* IT IS HIDDEN UNTIL WE HAVE CONTRACTS TO PROJECT */}
              <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
                <thead>
                  <tr className='text-start text-gray-400 fw-bold fs-8 text-uppercase gs-0'>
                    <th className='min-w-300px p-5 shadow-sm '>
                      {intl.formatMessage({id: 'ATTACHEMENT.TITLE'})}
                    </th>
                  </tr>
                </thead>
                {response?.data.map((rs) => {
                  return (
                    <tbody className='text-gray-600 fw-bold'>
                      <tr>
                        <td colSpan={1}>
                          <div className='d-flex align-items-center px-4'>
                            <KTSVG
                              path='/media/icons/duotune/files/fil003.svg'
                              className='svg-icon svg-icon-2x svg-icon-primary me-4'
                            />
                            <a href={rs.file} target='_blank'>
                              {rs.file_name}
                            </a>
                          </div>
                        </td>
                        <td colSpan={1}>
                          <button type='button' className='btn' onClick={() => deleta(rs.id)}>
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon svg-icon-2x svg-icon-primary me-4'
                            />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  )
                })}
              </table>
            </div>
          </div>
        </div>
      </form>
      {formik.isSubmitting}
    </>
  )
}

export {OverviewAttachement}
